import { DrawerFooter } from '@client/components/_core/layout/DrawerFooter';
import { OverlayPage } from '@client/components/_core/layout/OverlayPage';
import { PageController } from '@client/components/_core/layout/PageController';
import { message } from '@client/components/_ui/feedback/message';
import {
    SCHEDULE_INCOMPLETE_REASON,
    ScheduledAttestationForm,
} from '@client/components/attestations/scheduled/ScheduledAttestationForm';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useFormDirty } from '@client/hooks/forms/useFormDirty';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { trpc } from '@client/trpc/client';
import { createFileRoute } from '@tanstack/react-router';
import { useId, useRef, useState } from 'react';

export const Route = createFileRoute('/console/$orgId/attestations/scheduled-attestations/new')({
    staticData: {
        presentation: 'overlay',
        metadata: {
            title: 'New scheduled attestation',
        },
        permissions: ['user_attestation:create'],
        featureFlag: 'enable-scheduled-attestations',
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <NewScheduledAttestation />
        </PageController>
    );
}

function NewScheduledAttestation() {
    const formId = useId();
    const navigate = useNavigate();
    const { trpcUtils } = useTrpc();
    const onCloseRef = useRef<(() => void) | null>(null);
    const [isValid, setIsValid] = useState(false);
    const [saveBlockedReason, setSaveBlockedReason] = useState<string | undefined>(SCHEDULE_INCOMPLETE_REASON);
    const formDirty = useFormDirty();

    const { mutate: create, isPending } = trpc.userAttestation.createScheduledUserAttestation.useMutation({
        onSuccess: () => {
            message.success('Scheduled attestation saved');
            void trpcUtils.userAttestation.listScheduledUserAttestations.invalidate();
            onCloseRef.current?.();
        },
        onError: (error) => {
            message.error(error.message || 'Failed to save scheduled attestation');
        },
    });

    return (
        <OverlayPage
            title="New scheduled attestation"
            size="large"
            isDirty={formDirty.isDirty}
            afterClose={() => navigate({ moveInMatches: -1 })}
            footer={({ requestClose }) => (
                <DrawerFooter
                    onCancel={requestClose}
                    formId={formId}
                    okLoading={isPending}
                    okDisabled={!isValid}
                    okDisabledReason={saveBlockedReason ?? SCHEDULE_INCOMPLETE_REASON}
                    // Title case mandated by CUR-858 — an exception to the sentence-case rule.
                    okText="Save Scheduled Attestation"
                />
            )}
        >
            {({ onClose }) => (
                <ScheduledAttestationForm
                    mode="create"
                    formId={formId}
                    formDirty={formDirty}
                    onValidityChange={setIsValid}
                    onSaveBlockedReasonChange={setSaveBlockedReason}
                    onSubmit={(input) => {
                        onCloseRef.current = onClose;
                        create(input);
                    }}
                />
            )}
        </OverlayPage>
    );
}
